import { useEffect, useState, type ReactNode } from "react";
import { useVeri } from "@/lib/veri";
import { KimlikSecici } from "@/components/kimlik-secici";
import { Bildirimler } from "@/components/bildirimler";
import { Link, useRouterState } from "@tanstack/react-router";
import { BadgeCheck, ClipboardCheck, Library, Palette, UserCog, BookOpen, ChevronDown, ClipboardList, FileUp, FlaskConical, FolderInput, FolderOpen, Gauge, Grid3x3, Hammer, Inbox, Layers, LayoutDashboard, Menu, Package, PanelLeftClose, PanelLeftOpen, Search, Settings2, Ship, ShieldCheck, Wrench, X, LogOut, Users } from "lucide-react";
import { Logo } from "@/components/logo";
import { ThemeToggle } from "@/components/theme-toggle";
import { DilSecici } from "@/components/dil-secici";
import { useOturum } from "@/lib/oturum";
import { api } from "@/lib/api";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";

type Nav = { ad: string; yol: string; icon: React.ElementType; alt?: boolean; pasif?: boolean; sayac?: keyof Kuyruk };
type Kuyruk = { mrb: number; teslim: number; sokum: number; saha: number; dogrulama: number; planlama: number; assembly: number; islerim: number; belgeler: number };
type Grup = { baslik: string; items: Nav[] };

/** Melez A kabuğu: prototipin 8 başlığı süreç sırasıyla; alt görünümler girintili; sonraki bantlar soluk. */
const NAV: Grup[] = [
  {
    baslik: cevir("General"),
    items: [
      // D-071: salt okunur pano, ilk öğe ve açılış sayfası.
      { ad: cevir("Dashboard"), yol: "/", icon: LayoutDashboard },
      { ad: cevir("My work"), yol: "/islerim", icon: ClipboardCheck, sayac: "islerim" },
      // D-074 (U-073): bütün yüklemeler tek sayfada; sayaç kişinin "Needs me" sayısı.
      { ad: cevir("Documents"), yol: "/belgeler", icon: FileUp, sayac: "belgeler" },
    ],
  },
  {
    baslik: cevir("Process"),
    items: [
      { ad: "Admin", yol: "/admin", icon: Settings2 },
      { ad: "MRB intake", yol: "/mrb", icon: BookOpen, sayac: "mrb" },
      { ad: cevir("Receiving"), yol: "/teslim", icon: Package, sayac: "teslim" },
      { ad: cevir("Disassembly"), yol: "/sokum", icon: ClipboardList, sayac: "sokum" },
      { ad: cevir("Field list"), yol: "/saha", icon: Layers, sayac: "saha" },
      { ad: cevir("Inspection verification"), yol: "/dogrulama", icon: Search, sayac: "dogrulama" },
      { ad: cevir("Repair planning"), yol: "/planlama", icon: Wrench, sayac: "planlama" },
      { ad: cevir("Component"), yol: "/komponent", icon: FlaskConical, alt: true },
      { ad: "Fit-up", yol: "/fitup", icon: Hammer, alt: true },
      { ad: "Weld map", yol: "/weldmap", icon: BadgeCheck, alt: true },
      { ad: "Finishing", yol: "/assembly", icon: Ship, sayac: "assembly" },
      // D-058: riserin bütün belgeleri tek sayfada (salt okunur); sayaç yok
      { ad: cevir("Riser Documentation"), yol: "/riser-documentation", icon: FolderOpen },
      // D-059: riser dosyasının kendi ekranı olmayan belgeleri ve bağları buradan girilir; sayaç yok
      { ad: cevir("Project documents"), yol: "/project-documents", icon: FolderInput },
    ],
  },
  { baslik: cevir("System"), items: [{ ad: cevir("Users"), yol: "/kullanicilar", icon: Users }, { ad: cevir("Registers"), yol: "/kutukler", icon: Library }] },
  {
    baslik: cevir("Next bands (spec)"),
    items: [
      { ad: cevir("Station grid"), yol: "#", icon: Grid3x3, pasif: true },
      { ad: cevir("Hydrotest"), yol: "#", icon: Gauge, pasif: true },
      { ad: cevir("MRB package / DNV"), yol: "#", icon: ShieldCheck, pasif: true },
    ],
  },
];

/** D-056: ortam, adresteki alan adından. "test." ile başlayan test sitesi, localhost bu bilgisayar, ötekiler canlı. */
type Ortam = "test" | "yerel" | "canli";
const ortamBul = (alanAdi: string): Ortam => (alanAdi.startsWith("test.") ? "test" : ["localhost", "127.0.0.1", "[::1]"].includes(alanAdi) ? "yerel" : "canli");

/** D-056: üst çubukta ortam ve sürüm etiketi. Sürüm girişsiz açık /api/health'ten; gelmediyse canlıda çizilmez, test ve yerelde yalnız ortam adı. */
function OrtamEtiketi() {
  const { veri: saglik } = useVeri<{ surum: string }>("/health");
  const ortam = ortamBul(window.location.hostname);
  const kisa = saglik?.surum.slice(0, 7);
  if (ortam === "canli") return kisa ? <span title={cevir("Version")} className="hidden whitespace-nowrap text-xs text-ink-3 lg:inline">{kisa}</span> : null;
  return (
    <span title={cevir("Version")} className={cn("hidden items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium leading-4 lg:inline-flex", ortam === "test" ? "bg-warn-bg text-warn" : "bg-info-bg text-info")}>
      {ortam === "test" ? cevir("Test") : cevir("Local")}{kisa && ` · ${kisa}`}
    </span>
  );
}

export function Kabuk({ children }: { children: ReactNode }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { ben, cikis, yenile } = useOturum();
  const kimligiBirak = async () => {
    await api.post("/auth/kimlik-birak");
    await yenile();
  };
  const yol = useRouterState({ select: (s) => s.location.pathname });
  const { veri: kuyruk } = useVeri<Kuyruk>(ben ? "/kuyruk" : null);
  const aktif = (n: Nav) => n.yol !== "#" && (yol === n.yol || yol.startsWith(n.yol + "/"));

  useEffect(() => setMobileOpen(false), [yol]);

  const NavList = ({ mobil }: { mobil?: boolean }) => (
    <nav className="scroll-thin flex-1 overflow-y-auto py-2">
      {NAV.map((g) => (
        <div key={g.baslik} className="mb-2">
          {(!collapsed || mobil) && <div className="px-4 pb-1 pt-3 text-2xs font-semibold uppercase tracking-[0.08em] text-ink-3">{g.baslik}</div>}
          {g.items.map((it) => {
            const on = aktif(it);
            const cls = cn(
              "group relative mx-2 my-0.5 flex items-center gap-2.5 rounded-md px-2.5 text-base text-ink-2 hover:bg-surface-2 hover:text-ink",
              mobil ? "py-2.5 text-base" : "py-[7px]",
              on && "bg-surface-2 font-semibold text-ink",
              it.alt && (!collapsed || mobil) && "ml-6 py-[5px] text-sm",
              it.pasif && "pointer-events-none opacity-50",
              collapsed && !mobil && "justify-center px-0",
            );
            const ic = (
              <>
                {on && <span className="absolute bottom-1.5 left-0 top-1.5 w-[3px] rounded-r bg-orange" />}
                <it.icon size={mobil ? 18 : 16} className={cn("shrink-0", on ? "text-orange" : "text-ink-3 group-hover:text-ink-2")} />
                {(!collapsed || mobil) && <span className="flex-1 truncate">{it.ad}</span>}
                {it.sayac && kuyruk && kuyruk[it.sayac] > 0 && (
                  <span title={cevir`${kuyruk[it.sayac]} pending work`} className={cn("rounded-full bg-orange px-1.5 text-2xs font-semibold leading-[17px] text-white", collapsed && !mobil && "absolute right-1 top-1 px-1 leading-[15px]")}>{kuyruk[it.sayac]}</span>
                )}
              </>
            );
            return it.pasif ? <span key={it.ad} className={cls} title={cevir("Not written yet")}>{ic}</span> : <Link key={it.ad} to={it.yol} className={cls} title={it.ad}>{ic}</Link>;
          })}
        </div>
      ))}
    </nav>
  );

  return (
    <div className="font-plex min-h-dvh bg-bg text-ink">
      <aside className={cn("fixed inset-y-0 left-0 z-40 hidden flex-col border-r border-line bg-surface transition-[width] duration-200 lg:flex print:hidden", collapsed ? "w-[64px]" : "w-[248px]")}>
        <div className={cn("flex h-14 items-center border-b border-line", collapsed ? "justify-center" : "px-4")}>
          {collapsed ? <span className="font-manrope text-lg font-extrabold tracking-tight">O<span className="text-orange">|</span></span> : <Link to="/"><Logo height={22} /></Link>}
        </div>
        <NavList />
        <div className="border-t border-line p-2">
          <button onClick={() => setCollapsed((c) => !c)} className="flex w-full items-center justify-center gap-2 rounded-md px-2 py-2 text-xs text-ink-3 hover:bg-surface-2 hover:text-ink">
            {collapsed ? <PanelLeftOpen size={16} /> : <><PanelLeftClose size={16} /> {cevir("Collapse")}</>}
          </button>
        </div>
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 flex w-[84%] max-w-[300px] flex-col bg-surface shadow-[var(--shadow-lg)]">
            <div className="flex h-14 items-center justify-between border-b border-line px-4">
              <Logo height={22} />
              <button onClick={() => setMobileOpen(false)} className="rounded-md p-1.5 text-ink-2 hover:bg-surface-2"><X size={18} /></button>
            </div>
            <NavList mobil />
          </div>
        </div>
      )}

      <div className={cn("transition-[padding] duration-200 print:pl-0", collapsed ? "lg:pl-[64px]" : "lg:pl-[248px]")}>
        <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-line bg-surface/90 px-3 backdrop-blur sm:px-4 print:hidden">
          <button onClick={() => setMobileOpen(true)} className="rounded-md p-2 text-ink-2 hover:bg-surface-2 lg:hidden" aria-label={cevir("Menu")}><Menu size={20} /></button>
          <Link to="/" className="lg:hidden"><Logo height={20} /></Link>
          <div className="ml-auto flex items-center gap-1.5">
            <KimlikSecici />
            <span className="hidden items-center gap-1.5 rounded-full border border-line bg-surface px-2.5 py-1 text-xs text-ink-2 sm:inline-flex"><span className="h-1.5 w-1.5 rounded-full bg-ok" /> {cevir("Cycle 2024–29")}</span>
            <OrtamEtiketi />
          </div>
          <Bildirimler />
          <DilSecici />
          <ThemeToggle />
          {ben && (
            <div className="group relative">
              <button className="flex h-9 items-center gap-2 rounded-md border border-line bg-surface pl-1.5 pr-2 hover:border-line-strong">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-navy text-2xs font-bold text-white dark:text-[#0a141c]">{ben.ad.split(" ").map((s) => s[0]).join("").slice(0, 2)}</span>
                <span className="hidden text-left sm:block"><span className="block text-sm font-semibold leading-tight">{ben.ad.split(" ")[0]}</span><span className="block text-2xs leading-tight text-ink-3">{cevir(ben.rolEtiket)}</span></span>
                <ChevronDown size={14} className="text-ink-3" />
              </button>
              <div className="invisible absolute right-0 top-full z-40 mt-1 w-60 rounded-md border border-line bg-surface p-1 opacity-0 shadow-[var(--shadow-lg)] transition group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                <div className="px-2 py-2"><div className="text-sm font-semibold">{ben.ad}</div><div className="text-xs text-ink-3">{ben.eposta} · {cevir(ben.rolEtiket)}</div></div>
                {ben?.konsept && (
                  <Link to="/konsept" className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-base hover:bg-surface-2"><Palette size={14} /> {cevir("Concept views")}</Link>
                )}
                <button onClick={() => void cikis()} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-base hover:bg-surface-2"><LogOut size={14} /> {cevir("Log out")}</button>
              </div>
            </div>
          )}
        </header>
        {ben?.asil && (
          <div className="flex flex-wrap items-center gap-2 border-b border-orange/40 bg-orange-soft px-4 py-2 text-sm print:hidden">
            <UserCog size={15} className="text-orange" />
            <span><b>{ben.ad}</b> ({cevir(ben.rolEtiket)}{cevir(") — you are viewing with this Identity; actual account:")} {ben.asil.ad}.</span>
            <button onClick={() => void kimligiBirak()} className="ml-auto font-semibold text-orange underline">{cevir("Back to my own account")}</button>
          </div>
        )}
        <main className="mx-auto w-full max-w-[1480px] px-3 pb-24 pt-4 sm:px-5 lg:pb-8 print:pb-0">{children}</main>
      </div>

      <nav className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-5 border-t border-line bg-surface/95 backdrop-blur lg:hidden print:hidden" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        {[
          { ad: cevir("Dashboard"), yol: "/", icon: LayoutDashboard },
          { ad: cevir("Field"), yol: "/saha", icon: Layers },
          { ad: cevir("Verification"), yol: "/dogrulama", icon: Search },
          { ad: cevir("Planning"), yol: "/planlama", icon: Wrench },
          { ad: cevir("Menu"), yol: "#", icon: Menu },
        ].map((t) =>
          t.yol === "#" ? (
            <button key={t.ad} onClick={() => setMobileOpen(true)} className="flex flex-col items-center gap-0.5 py-2 text-2xs text-ink-3"><t.icon size={20} />{t.ad}</button>
          ) : (
            <Link key={t.ad} to={t.yol} className={cn("flex flex-col items-center gap-0.5 py-2 text-2xs", (t.yol === "/" ? yol === "/" : yol.startsWith(t.yol)) ? "font-semibold text-orange" : "text-ink-3")}><t.icon size={20} />{t.ad}</Link>
          ),
        )}
      </nav>
    </div>
  );
}
